import React, { useEffect, useState, useMemo } from "react";
import { Input, Segmented, Select, DatePicker } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { servicesKeys } from "../../../Receptionist/receptionKeys";
import ServicesIpTable from "./ServicesIpTable";
import AssignedServicesTable from "./AssignedServicesTable";
import { getCategoryApi } from "../../../../services/service.services";
import { debounce } from "../../../../helpers/utility";
import { UiButton, UiTag } from "../../../ui";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import useReceptionistMobilePickerProps from "../../../Receptionist/receptionistMobilePickerProps";
import useQuery from "../../../../hooks/useQuery";
import {
  parseSearchParam,
  updateUrlSearchParams,
} from "../../../../helpers/urlFilters";
import styles from "./ServicesTabs.module.css";

const ASSIGNED_STATUS_OPTIONS = [
  { label: "Ordered", value: "Ordered" },
  { label: "Done", value: "Done" },
  { label: "Cancelled", value: "Cancelled" },
  { label: "Delete Requested", value: "Delete_Requested" },
];

const ServicesTabs = ({ patientData }) => {
  const navigate = useNavigate();
  const params = useQuery();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const urlSegment = params?.view || "all_services";
  const [segmentView, setSegmentView] = useState(urlSegment);
  const [mountedViews, setMountedViews] = useState(() => ({
    all_services: urlSegment === "all_services",
    assigned_services: urlSegment === "assigned_services",
  }));
  const searchQuery = parseSearchParam(params);
  const categoryFilter = params?.category ? Number(params.category) : null;
  const statusFilter = params?.service_status || null;
  const dateFilter = params?.service_date
    ? dayjs(params.service_date, "YYYY-MM-DD")
    : null;
  const [categories, setCategories] = useState([]);
  const [serviceCount, setServiceCount] = useState(0);
  const [assignedReloadToken, setAssignedReloadToken] = useState(0);
  const reloadAssignedServices = () =>
    setAssignedReloadToken((token) => token + 1);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    setSegmentView(urlSegment);
    setMountedViews((prev) => ({
      ...prev,
      [urlSegment]: true,
    }));
  }, [urlSegment]);

  useEffect(() => {
    getCategoryApi()
      .then((res) => setCategories(res?.data ?? []))
      .catch((err) => console.log(err));
  }, []);

  const [searchInput, setSearchInput] = useState(searchQuery || "");

  useEffect(() => {
    setSearchInput(searchQuery || "");
  }, [searchQuery]);

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        updateUrlSearchParams(navigate, { search: value || null, page: 1 });
      }, 300),
    [navigate]
  );

  const onSearch = (e) => {
    const value = e?.target?.value ?? "";
    setSearchInput(value);
    debouncedSetSearchQuery(value);
  };

  const onSegmentChange = (value) => {
    if (value === segmentView) return;
    setSegmentView(value);
    if (value === "assigned_services" && mountedViews.assigned_services) {
      reloadAssignedServices();
    }
    setMountedViews((prev) => ({ ...prev, [value]: true }));
    updateUrlSearchParams(navigate, {
      view: value,
      search: null,
      category: null,
      service_status: null,
      service_date: null,
      page: 1,
    });
    setServiceCount(0);
  };

  const clearFilters = () => {
    if (segmentView === "assigned_services") {
      updateUrlSearchParams(navigate, {
        search: null,
        service_status: null,
        service_date: null,
        page: 1,
      });
      return;
    }
    updateUrlSearchParams(navigate, {
      search: null,
      category: null,
      page: 1,
    });
  };

  const hasActiveFilters =
    segmentView === "assigned_services"
      ? Boolean(searchQuery || statusFilter || dateFilter)
      : Boolean(searchQuery || categoryFilter);

  return (
    <div className={styles.root}>
      <div className={styles.panelHeader}>
        <Segmented
          size="large"
          className={styles.segmented}
          options={servicesKeys?.map((item) => ({
            label: item?.name,
            value: item?.id,
          }))}
          value={segmentView}
          onChange={onSegmentChange}
        />
      </div>

      <div className={styles.panelToolbar}>
        <div className={styles.searchGroup}>
          <Input
            className={styles.searchInput}
            size="middle"
            allowClear
            prefix={<SearchOutlined className={styles.searchIcon} />}
            placeholder="Search for Services"
            value={searchInput}
            onChange={onSearch}
          />
          <UiTag tone="info" size="sm" className={styles.searchCount}>
            {serviceCount} Services
          </UiTag>
        </div>

        <div className={styles.filtersGroup}>
          {segmentView === "all_services" ? (
            <Select
              className={`${inputStyles.mdSelect} ${styles.filterControl} ${styles.filterSelect}`}
              size="middle"
              placeholder="All categories"
              value={categoryFilter}
              onChange={(value) =>
                updateUrlSearchParams(navigate, {
                  category: value,
                  page: 1,
                })
              }
              allowClear
              popupMatchSelectWidth={false}
            >
              {categories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <>
              <DatePicker
                className={`${inputStyles.mdDateInput} ${styles.filterControl} ${styles.filterSelect} ${
                  isMobile ? styles.dateFilterMobile : ""
                }`}
                size="middle"
                placeholder="Select a Date"
                value={dateFilter}
                onChange={(value) =>
                  updateUrlSearchParams(navigate, {
                    service_date: value
                      ? value.format("YYYY-MM-DD")
                      : null,
                    page: 1,
                  })
                }
                allowClear
                format="DD-MM-YYYY"
                inputReadOnly={isMobile}
                {...mobilePickerProps}
              />
              <Select
                className={`${inputStyles.mdSelect} ${styles.filterControl} ${styles.filterSelect}`}
                size="middle"
                placeholder="All Statuses"
                value={statusFilter}
                onChange={(value) =>
                  updateUrlSearchParams(navigate, {
                    service_status: value,
                    page: 1,
                  })
                }
                allowClear
                popupMatchSelectWidth={false}
              >
                {ASSIGNED_STATUS_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </>
          )}
          <UiButton
            variant="secondary"
            size="md"
            className={styles.clearBtn}
            icon={<CloseOutlined />}
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear Filters
          </UiButton>
        </div>
      </div>

      <div className={styles.servicesPanel}>
        <div className={styles.panelBody}>
          {mountedViews.all_services && (
            <div
              className={
                segmentView !== "all_services" ? styles.hiddenPanel : undefined
              }
            >
              <ServicesIpTable
                patientData={patientData}
                searchQuery={searchQuery}
                categoryFilter={categoryFilter}
                onCountChange={setServiceCount}
                onServiceAssigned={reloadAssignedServices}
              />
            </div>
          )}
          {mountedViews.assigned_services && (
            <div
              className={
                segmentView !== "assigned_services"
                  ? styles.hiddenPanel
                  : undefined
              }
            >
              <AssignedServicesTable
                patientData={patientData}
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                dateFilter={dateFilter}
                onCountChange={setServiceCount}
                reloadToken={assignedReloadToken}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesTabs;
